import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Brain, Download, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { ASSESSMENT_CATEGORIES } from './categories';
import { getActiveCategories, getQuestionPoolStats } from './enhancedCategories';
import { FEATURE_FLAGS } from '../../engine/enhancers/questionEnhancer';
import { calculateAdvancedScores } from './advancedScoring';
import { calculateEnhancedProfile } from '../../engine/scoring/enhancedProfile';
import EnhancedScoreDisplay from './components/EnhancedScoreDisplay';
import ScoringToggle from './components/ScoringToggle';
import { QuestionTransition, LiquidProgress, AnswerOption, Confetti, CountUp } from '../../components/ui';
import ProctorCamera from '../../components/ProctorCamera';
import ProctoringAlertBanner from '../../components/assessment/ProctoringAlertBanner';
import ProctoringStatusPanel from '../../components/assessment/ProctoringStatusPanel';
import EnhancementStats from './components/EnhancementStats';
import AIEnhancementBadge from './components/AIEnhancementBadge';
import AIPerformanceIndicator from './components/AIPerformanceIndicator';
import AdaptiveDifficultyIndicator from './components/AdaptiveDifficultyIndicator';
import HeatmapInsights from './components/HeatmapInsights';
import EnhancedQuestionTransitions, { DifficultyShiftTransition } from './components/EnhancedQuestionTransitions';
import { useQuestionSelector } from './hooks/useQuestionSelector';
import useEnterpriseProctoring from '../../hooks/useEnterpriseProctoring';
import { initLMS, trackProgress, completeLMS } from '../../engine/lms/lmsIntegration';

const ExtendedAssessment = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completedCategories, setCompletedCategories] = useState({});
  const [categoryResults, setCategoryResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [exitReason, setExitReason] = useState(null);
  const [askedQuestions, setAskedQuestions] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [performanceData, setPerformanceData] = useState({
    easy: 85,
    medium: 72,
    hard: 58
  });
  const [engagementScore, setEngagementScore] = useState(78);
  const [lastAnswerTimestamp, setLastAnswerTimestamp] = useState(Date.now());
  const videoRef = useRef(null);
  const testStreamRef = useRef(null);
  const forceSubmitRef = useRef(() => {});
  
  // AI Question Selector
  const questionSelector = useQuestionSelector(selectedCategory, answers, askedQuestions);

  // Load saved progress from localStorage on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('cogniq_assessment_progress');
    if (savedProgress) {
      try {
        const { selectedCategory: savedCategory, answers: savedAnswers, currentQuestionIndex: savedIndex, timestamp } = JSON.parse(savedProgress);
        // Only restore if saved within last 24 hours
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          setSelectedCategory(savedCategory);
          setAnswers(savedAnswers);
          setCurrentQuestionIndex(savedIndex);
          if (savedCategory) setCurrentView('assessment');
          toast.success('Progress restored from last session');
        } else {
          localStorage.removeItem('cogniq_assessment_progress');
        }
      } catch (err) {
        console.error('Failed to restore progress:', err);
      }
    }
  }, []);

  // Auto-save progress to localStorage whenever answers change
  useEffect(() => {
    if (selectedCategory && Object.keys(answers).length > 0) {
      const progressData = {
        selectedCategory,
        answers,
        currentQuestionIndex,
        timestamp: Date.now()
      };
      localStorage.setItem('cogniq_assessment_progress', JSON.stringify(progressData));
    }
  }, [answers, selectedCategory, currentQuestionIndex]);

  useEffect(() => {
    // ADDED: hidden webcam stream for enterprise proctoring.
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        testStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        // Proctoring degrades gracefully if camera stream cannot be started here.
      });

    return () => {
      if (testStreamRef.current) {
        testStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);
  
  // ADDED: enterprise proctoring for unified monitoring across assessments.
  const {
    proctoringReport,
    currentAlert,
    dismissAlert,
    violations,
    integrityScore,
    faceStatus,
    gazeStatus,
    isFullscreen,
    requestFullscreen,
    recordAnswer
  } = useEnterpriseProctoring({
    assessmentKey: 'extended',
    videoRef,
    enabled: true,
    onViolation: (type, detail) => {
      console.warn('[Proctoring]', type, detail);
    },
    onForcedSubmit: () => forceSubmitRef.current?.(),
    maxViolations: 10
  });

  const category = selectedCategory ? ASSESSMENT_CATEGORIES.find(c => c.id === selectedCategory) : null;
  const currentQuestion = category?.questions[currentQuestionIndex];

  const startCategory = async (categoryId) => {
    // Prevent assessment start if camera is not active
    if (!cameraActive) {
      toast.error('Camera access is required to start the assessment');
      return;
    }
    
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const cat = ASSESSMENT_CATEGORIES.find(c => c.id === categoryId);
      if (!cat) {
        throw new Error('Category not found');
      }
      
      setSelectedCategory(categoryId);
      setAnswers({});
      setCurrentQuestionIndex(0);
      setCurrentView('assessment');
      
      // Initialize LMS integration
      try {
        initLMS({
          assessmentId: `extended-${categoryId}`,
          xapi: {
            endpoint: import.meta.env.VITE_XAPI_ENDPOINT,
            auth: import.meta.env.VITE_XAPI_AUTH,
            actor: { mbox: 'mailto:user@example.com', name: 'User' },
            activityId: `http://cogniq.com/assessment/${categoryId}`
          }
        });
      } catch (err) {
        console.warn('LMS init failed:', err);
      }
      
      toast.success(`${cat.name} assessment started`);
    } catch (err) {
      toast.error(`Failed to start assessment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (value) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    setAskedQuestions(prev => [...prev, currentQuestion.id]);

    // ADDED: enterprise proctoring rapid-answer capture.
    recordAnswer(Date.now() - lastAnswerTimestamp);
    setLastAnswerTimestamp(Date.now());
    
    // Track progress in LMS
    try {
      trackProgress({
        questionId: currentQuestion.id,
        answer: value,
        progress: (currentQuestionIndex + 1) / (category?.questions.length || 1)
      });
    } catch (err) {
      console.warn('LMS tracking failed:', err);
    }
  };

  const handleNext = async () => {
    if (currentQuestionIndex < category.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const scores = calculateEnhancedProfile(answers, selectedCategory);
        
      const result = {
        category: category?.name || '',
        scores,
        timestamp: new Date().toLocaleString(),
        answers
      };
      
      try {
        completeLMS({
          score: scores.score || 0,
          status: 'completed'
        });
      } catch (err) {
        console.warn('LMS completion failed:', err);
      }
      
      setCompletedCategories(prev => ({ ...prev, [selectedCategory]: true }));
      setCategoryResults(prev => ({ ...prev, [selectedCategory]: result }));
      
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      
      setCurrentView('categoryReport');
      toast.success(`${category.name} assessment completed!`);
      localStorage.removeItem('cogniq_assessment_progress');
      setLoading(false);
    }
  };

  const completedCount = Object.keys(completedCategories).length;
  const totalCategories = ASSESSMENT_CATEGORIES.length;
  forceSubmitRef.current = () => {
    setCurrentView('dashboard');
    setExitReason('Assessment terminated due to excessive violations.');
    toast.error('Assessment terminated due to excessive violations.');
  };

  const handleCameraLost = (reason) => {
    if (currentView === 'assessment') {
      setCurrentView('dashboard');
      setExitReason(reason);
      setSelectedCategory(null);
      setCurrentQuestionIndex(0);
      setAnswers({});
    }
  };

  // Prevent assessment if camera not active
  useEffect(() => {
    if (!cameraActive && currentView === 'assessment') {
      setCurrentView('dashboard');
      setExitReason('Camera access is required for proctored assessment');
    }
  }, [cameraActive, currentView]);

  // Dashboard View
  if (currentView === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          aria-hidden="true"
        />
        <ProctoringAlertBanner message={currentAlert} onDismiss={dismissAlert} />
        <ProctoringStatusPanel
          faceStatus={faceStatus}
          gazeStatus={gazeStatus}
          violations={violations}
          integrityScore={integrityScore}
          isFullscreen={isFullscreen}
          onRequestFullscreen={requestFullscreen}
          videoRef={videoRef}
        />
        {/* Proctoring Components */}
        <ProctorCamera 
          onCameraStatus={setCameraActive} 
          onCameraLost={handleCameraLost}
        />
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Brain className="w-8 h-8 text-indigo-600 mr-3" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-700 to-purple-700 bg-clip-text text-transparent">
                Extended Assessment
              </h1>
            </div>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-4">
              Complete 7 comprehensive categories for deep insights into your professional profile.
            </p>
            
            {/* Camera Status Alert */}
            {!cameraActive && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto mb-6">
                <div className="flex items-center">
                  <div className="text-red-600 mr-3">📹</div>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Camera Access Required</h3>
                    <p className="text-sm text-red-600 mt-1">
                      Please enable camera access to start the proctored assessment.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Exit Reason Alert */}
            {exitReason && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto mb-6">
                <div className="flex items-center">
                  <div className="text-yellow-600 mr-3">⚠️</div>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Assessment Interrupted</h3>
                    <p className="text-sm text-yellow-600 mt-1">{exitReason}</p>
                    <button 
                      onClick={() => setExitReason(null)}
                      className="text-xs text-yellow-700 underline mt-1"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Progress Indicator */}
            <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Progress</span>
                <span className="text-sm font-bold text-indigo-600">{completedCount}/{totalCategories}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(completedCount / totalCategories) * 100}%` }}
                ></div>
              </div>
              {completedCount === totalCategories && (
                <button
                  onClick={() => setCurrentView('finalReport')}
                  className="mt-4 w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-2 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  View Complete Report
                </button>
              )}
            </div>
          </motion.div>

          <EnhancementStats />
          
          {/* Performance Heatmap */}
          <HeatmapInsights
            performanceData={performanceData}
            categoryMastery={Object.fromEntries(
              Object.entries(categoryResults).map(([id, result]) => [
                ASSESSMENT_CATEGORIES.find(c => c.id === id)?.name || id,
                result.scores.score
              ])
            )}
            engagementScore={engagementScore}
            className="mb-8"
          />
          
          <ScoringToggle className="mb-8" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getActiveCategories().map((cat, index) => {
              const isCompleted = completedCategories[cat.id];
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ y: -4, shadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className={`rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer ${isCompleted ? 'ring-2 ring-emerald-500' : ''}`}
                  onClick={() => !loading && cameraActive && startCategory(cat.id)}
                >
                  <div className={`${cat.color} p-6 text-white text-center bg-gradient-to-br relative`}>
                    {isCompleted && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div className="text-4xl mb-2">{cat.icon}</div>
                    <h3 className="font-bold text-lg">{cat.name}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4 leading-relaxed">{cat.description}</p>
                    <AIEnhancementBadge question={cat.questions?.[0]} className="mb-4" />
                    <div className="text-xs text-slate-500 mb-4">
                      {cat.questions.length} questions
                      {FEATURE_FLAGS.AI_SELECTION && cat.totalQuestionPool && (
                        <span className="text-indigo-600"> • {cat.totalQuestionPool} AI pool</span>
                      )}
                      <span> • ~{Math.ceil(cat.questions.length * 0.5)} minutes</span>
                    </div>
                    <div className={`text-white py-2 px-4 rounded-lg text-sm font-medium text-center transition-all duration-200 flex items-center justify-center ${
                      !cameraActive 
                        ? 'bg-gray-400 cursor-not-allowed'
                        : isCompleted 
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' 
                        : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                    }`}>
                      {!cameraActive ? (
                        'Camera Required'
                      ) : isCompleted ? (
                        <>
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Retake Assessment
                        </>
                      ) : (
                        'Start Assessment'
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Assessment View
  if (currentView === 'assessment' && category && currentQuestion) {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          aria-hidden="true"
        />
        <ProctoringAlertBanner message={currentAlert} onDismiss={dismissAlert} />
        <ProctoringStatusPanel
          faceStatus={faceStatus}
          gazeStatus={gazeStatus}
          violations={violations}
          integrityScore={integrityScore}
          isFullscreen={isFullscreen}
          onRequestFullscreen={requestFullscreen}
          videoRef={videoRef}
        />
        {/* Proctoring Components */}
        <ProctorCamera 
          onCameraStatus={setCameraActive} 
          onCameraLost={handleCameraLost}
        />
        
        {/* AI Performance Indicator - Hidden on mobile */}
        <AIPerformanceIndicator
          performance={Object.keys(answers).length / Math.max(category.questions.length, 1)}
          targetDifficulty={currentQuestion?.difficulty || 'medium'}
          totalQuestions={category.questions.length}
          answeredQuestions={Object.keys(answers).length}
          className="hidden lg:block fixed bottom-4 left-4 z-40"
        />
        
        <div className="max-w-4xl mx-auto px-4 flex-1 flex flex-col justify-center">
          <motion.div 
            className="text-center mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-2">
              <div className={`w-8 h-8 rounded-lg ${category.color} text-white flex items-center justify-center mr-3`}>
                {category.icon}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-slate-600">
              Question {currentQuestionIndex + 1} of {category.questions.length}
            </p>
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setCurrentView('dashboard')}
                className="flex items-center space-x-2 text-slate-600 hover:text-indigo-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Categories</span>
              </button>
            </div>
          </motion.div>

          <div className="mb-6">
            <LiquidProgress 
              progress={((currentQuestionIndex + (answers[currentQuestion.id] ? 1 : 0)) / category.questions.length) * 100}
            />
          </div>

          <Confetti show={showConfetti} />
          
          <div className="relative">
            <AdaptiveDifficultyIndicator
              difficulty={currentQuestion.difficulty || 'medium'}
              isFollowUp={currentQuestion.isFollowUp || false}
              isInsightMode={questionSelector.isInsightMode || false}
              className="absolute inset-0 z-0"
            />
            
            <QuestionTransition questionId={currentQuestion.id}>
              <DifficultyShiftTransition
                questionId={currentQuestion.id}
                difficulty={currentQuestion.difficulty || 'medium'}
              >
                <div className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl p-8 mb-6 relative z-10">
                <div className="mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 leading-relaxed">{currentQuestion.text}</h2>
                </div>

            {/* Likert Scale Questions */}
            {currentQuestion.type === 'likert' && (
              <div className="space-y-2 md:space-y-3">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    onClick={() => handleAnswer(value)}
                    className={`w-full text-left p-3 md:p-4 rounded-lg border-2 transition-all duration-200 min-h-[48px] md:min-h-[44px] font-medium text-base md:text-sm ${
                      answers[currentQuestion.id] === value
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-400 hover:bg-indigo-50'
                    }`}
                  >
                    {value === 1 && 'Strongly Disagree'}
                    {value === 2 && 'Disagree'}
                    {value === 3 && 'Neutral'}
                    {value === 4 && 'Agree'}
                    {value === 5 && 'Strongly Agree'}
                  </button>
                ))}
              </div>
            )}

            {/* Choice Questions (Forced-Choice) */}
            {currentQuestion.type === 'choice' && currentQuestion.options && currentQuestion.options.length > 0 && (
              <div className="space-y-2 md:space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(index)}
                    className={`w-full text-left p-3 md:p-4 rounded-lg border-2 transition-all duration-200 min-h-[48px] md:min-h-[44px] ${
                      answers[currentQuestion.id] === index
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-400 hover:bg-indigo-50'
                    }`}
                  >
                    <span className="text-base md:text-sm">{option}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Multiple Choice Questions */}
            {currentQuestion.type === 'mcq' && currentQuestion.options && currentQuestion.options.length > 0 && (
              <div className="space-y-2 md:space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(index)}
                    className={`w-full text-left p-3 md:p-4 rounded-lg border-2 transition-all duration-200 min-h-[48px] md:min-h-[44px] ${
                      answers[currentQuestion.id] === index
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-400 hover:bg-indigo-50'
                    }`}
                  >
                    <span className="font-medium mr-2">{String.fromCharCode(65 + index)}.</span>
                    <span className="text-base md:text-sm">{option}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Text/Open-ended Questions - fallback for questions without options */}
            {((currentQuestion.type === 'text' || currentQuestion.type === 'video') || 
              (currentQuestion.type === 'mcq' && (!currentQuestion.options || currentQuestion.options.length === 0))) && (
              <div className="space-y-3">
                <textarea
                  className="w-full p-3 md:p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all min-h-[120px] text-base md:text-sm"
                  rows="5"
                  placeholder="Type your detailed response here..."
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey && e.target.value.trim()) {
                      e.preventDefault();
                      handleNext();
                    }
                  }}
                />
                <p className="text-xs text-slate-500 text-right">Press Ctrl+Enter to continue</p>
              </div>
            )}
              </div>
              </DifficultyShiftTransition>
            </QuestionTransition>
          </div>

          <motion.div 
            className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <button
              onClick={() => setCurrentQuestionIndex(i => Math.max(0, i - 1))}
              disabled={currentQuestionIndex === 0}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-12 sm:h-11 px-8 border-2 border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 disabled:opacity-50 disabled:pointer-events-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>Previous</span>
            </button>
            
            {currentQuestionIndex === category.questions.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={answers[currentQuestion.id] === undefined || loading}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-12 sm:h-11 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:pointer-events-none"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span>Complete Section</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={answers[currentQuestion.id] === undefined}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-12 sm:h-11 px-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:pointer-events-none"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // Category Report View
  if (currentView === 'categoryReport') {
    const result = categoryResults[selectedCategory];
    if (!result) return null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => setCurrentView('dashboard')}
              className="flex items-center space-x-2 text-slate-600 hover:text-indigo-600 transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Categories</span>
            </button>

            <div className="text-center">
              <div className="inline-block p-4 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full mb-4">
                <span className="text-4xl">{category.icon}</span>
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">
                {result.category} Results
              </h1>
              <p className="text-slate-600">Completed on {result.timestamp}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl p-8 mb-6 text-center"
          >
            <div className="text-6xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
              <CountUp end={Math.round(result.scores.score)} duration={1.5} />
            </div>
            <div className="text-slate-600 text-lg font-medium">/ 100 Score</div>
          </motion.div>
          
          <Confetti show={true} duration={2000} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl p-8"
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-700 to-purple-700 bg-clip-text text-transparent mb-6">AI Insights</h2>
            <ul className="space-y-4">
              {result.scores.insights.map((insight, i) => (
                <motion.li 
                  key={i} 
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{insight}</span>
                </motion.li>
              ))}
            </ul>
            
            {/* Enhanced Score Display */}
            <EnhancedScoreDisplay scores={result.scores} className="mt-6" />
           
            <div className="mt-8 flex gap-4 flex-wrap">
              <button 
                onClick={() => setCurrentView('dashboard')}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium h-11 px-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Continue Assessment
              </button>
              {completedCount === totalCategories && (
                <button
                  onClick={() => setCurrentView('finalReport')}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium h-11 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  View Complete Report
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Final Report View
  if (currentView === 'finalReport') {
    const overallScore = Math.round(
      Object.values(categoryResults).reduce((sum, result) => sum + result.scores.score, 0) / totalCategories
    );

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => setCurrentView('dashboard')}
              className="flex items-center space-x-2 text-slate-600 hover:text-indigo-600 transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>

            <div className="text-center">
              <div className="inline-block p-4 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full mb-4">
                <span className="text-4xl">🏆</span>
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">
                Complete Extended Assessment Report
              </h1>
              <p className="text-slate-600">All 7 categories completed • Generated on {new Date().toLocaleDateString()}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl p-8 mb-6 text-center"
          >
            <div className="text-6xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
              {overallScore}
            </div>
            <div className="text-slate-600 text-lg font-medium">/ 100 Overall Score</div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {ASSESSMENT_CATEGORIES.map((cat, index) => {
              const result = categoryResults[cat.id];
              if (!result) return null;
              
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 300
                  }}
                  whileHover={{ 
                    y: -4, 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                  className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl p-6"
                >
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 rounded-lg ${cat.color} text-white flex items-center justify-center mr-3`}>
                      {cat.icon}
                    </div>
                    <h3 className="font-bold text-gray-900">{cat.name}</h3>
                  </div>
                  <motion.div 
                    className="text-3xl font-bold text-indigo-600 mb-2"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.3, type: "spring" }}
                  >
                    {Math.round(result.scores.score)}
                  </motion.div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <motion.div
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${result.scores.score}%` }}
                      transition={{ delay: index * 0.1 + 0.5, duration: 0.8 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/50 shadow-xl p-8 text-center"
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-700 to-purple-700 bg-clip-text text-transparent mb-6">
              Download Your Complete Report
            </h2>
            <p className="text-slate-600 mb-8">
              Get a comprehensive PDF report with detailed insights, recommendations, and analysis across all 7 categories.
            </p>
            <button className="inline-flex items-center justify-center rounded-md text-sm font-medium h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all duration-200">
              <Download className="w-5 h-5 mr-2" />
              Download Complete PDF Report
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default ExtendedAssessment;
